"use client";

import Link from "next/link";
import Script from "next/script";
import React, { useEffect, useState } from "react";

export interface WContactUsSectionProps {
  title?: string;
  /** Anchor id for in-page links (from slug in Sanity). e.g. get-free-assessment → #get-free-assessment */
  sectionAnchor?: string;
  /** When true, CTA buttons targeting #get-free-assessment will scroll to this section (only one per page) */
  isPrimaryAnchor?: boolean;
  buttonLabel?: string;
  recipientEmail?: string;
  uploadImagesLabel?: string;
  uploadImagesHint?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  leftImageAlt?: string;
  rightImageAlt?: string;
}

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

const MAX_IMAGES = 5;
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_ACCEPT = "image/jpeg,image/png,image/webp";

const DEFAULT_SECTION_ANCHOR = "get-free-assessment";

export default function WContactUsSection({
  title = "Request a Consultation",
  sectionAnchor = DEFAULT_SECTION_ANCHOR,
  isPrimaryAnchor = false,
  buttonLabel = "Submit",
  recipientEmail,
  uploadImagesLabel = "Upload Images (optional)",
  uploadImagesHint = "JPG, PNG or WebP. Max 5 images, 5MB each. Sent securely with your request.",
  leftImageUrl,
  rightImageUrl,
  leftImageAlt = "Left cover",
  rightImageAlt = "Right cover",
}: WContactUsSectionProps) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
  });
  const [images, setImages] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

  useEffect(() => {
    if (typeof window === "undefined" || !sectionAnchor) return;
    const hash = `#${sectionAnchor}`;
    if (window.location.hash !== hash) return;
    const el = document.getElementById(sectionAnchor);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sectionAnchor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) {
      setImages([]);
      return;
    }
    const valid: File[] = [];
    for (let i = 0; i < Math.min(files.length, MAX_IMAGES); i++) {
      const file = files[i];
      if (file.size <= MAX_FILE_BYTES && /^image\/(jpeg|png|webp)$/i.test(file.type)) {
        valid.push(file);
      }
    }
    setImages(valid);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (typeof grecaptcha === "undefined") {
      setErrorMessage("reCAPTCHA is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, { action: "submit" });

        const body = new FormData();
        body.append("token", token);
        body.append("name", formData.name.trim());
        body.append("phone", formData.phone.trim());
        body.append("email", formData.email.trim());
        body.append("city", formData.city.trim());
        if (recipientEmail?.trim()) {
          body.append("recipientEmail", recipientEmail.trim());
        }
        images.forEach((file) => body.append("images", file));

        const response = await fetch("/api/send-free-assessment", {
          method: "POST",
          body,
        });

        if (response.ok) {
          const data = await response.json();
          setSuccessMessage(data.message ?? "Form processed successfully!");
          setSubmitted(true);
          setFormData({ name: "", phone: "", email: "", city: "" });
          setImages([]);

          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "trichos_form_submit",
              formType: "freeAssessment",
              formId: "trichos-contact-us-form",
              formName: "Get Free Assessment",
              submissionStatus: "success",
            });
          }

          setTimeout(() => {
            window.location.href = "/thank-you-consultation";
          }, 1500);
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message ?? "Error submitting form.");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    });
  };

  const hasImages = Boolean(leftImageUrl || rightImageUrl);

  return (
    <section
      id={sectionAnchor}
      {...(isPrimaryAnchor && { "data-section": "w-contact-us-form" })}
      className={`relative w-full overflow-hidden scroll-mt-20 ${hasImages ? "min-h-screen" : ""}`}
      aria-label={title || "Request a consultation"}
    >
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />

      {hasImages ? (
        <>
          {/* Mobile: single column (left image only). Desktop: two columns (left + right images). Right image hidden on mobile. */}
          <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
            <div
              className="min-h-screen bg-gray-200"
              style={
                leftImageUrl
                  ? {
                      backgroundImage: `url(${leftImageUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : undefined
              }
              role={leftImageUrl ? "img" : undefined}
              aria-label={leftImageUrl ? leftImageAlt : undefined}
            />
            <div
              className="hidden min-h-screen bg-gray-300 md:block md:opacity-30"
              style={
                rightImageUrl
                  ? {
                      backgroundImage: `url(${rightImageUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : undefined
              }
              role={rightImageUrl ? "img" : undefined}
              aria-label={rightImageUrl ? rightImageAlt : undefined}
            />
          </div>

          {/* Form box: just right of center, still overlaying the left column. Left padding 20px. */}
          <div className="absolute left-1/2 top-1/2 w-full max-w-lg -translate-x-[calc(50%-0.5rem)] -translate-y-1/2 px-4 md:-translate-x-[calc(50%-1rem)] md:px-0">
            <div className="rounded-xl border border-violet-200 bg-white/95 py-8 pl-5 pr-5 shadow-xl backdrop-blur-sm md:py-10 md:pl-5 md:pr-7">
              <h2 className="mb-6 text-xl font-bold text-violet-950 md:text-2xl">
                {title}
              </h2>
              {submitted ? (
                <div className="text-center">
                  {successMessage && (
                    <p className="text-lg text-green-600">{successMessage}</p>
                  )}
                  {errorMessage && (
                    <p className="text-lg text-red-600">{errorMessage}</p>
                  )}
                </div>
              ) : (
                <FormContent
                  formData={formData}
                  images={images}
                  loading={loading}
                  errorMessage={errorMessage}
                  buttonLabel={buttonLabel}
                  uploadImagesLabel={uploadImagesLabel}
                  uploadImagesHint={uploadImagesHint}
                  handleChange={handleChange}
                  handleFileChange={handleFileChange}
                  handleSubmit={handleSubmit}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        /* No images: simple centered form */
        <div className="rounded-xl bg-white px-4 py-8 shadow-sm md:px-6 md:py-10">
          <h2 className="mb-6 text-center text-xl font-bold text-violet-950 md:text-2xl">
            {title}
          </h2>
          {submitted ? (
            <div className="text-center">
              {successMessage && (
                <p className="text-lg text-green-600">{successMessage}</p>
              )}
              {errorMessage && (
                <p className="text-lg text-red-600">{errorMessage}</p>
              )}
            </div>
          ) : (
            <FormContent
              formData={formData}
              images={images}
              loading={loading}
              errorMessage={errorMessage}
              buttonLabel={buttonLabel}
              uploadImagesLabel={uploadImagesLabel}
              uploadImagesHint={uploadImagesHint}
              handleChange={handleChange}
              handleFileChange={handleFileChange}
              handleSubmit={handleSubmit}
            />
          )}
        </div>
      )}
    </section>
  );
}

function FormContent({
  formData,
  images,
  loading,
  errorMessage,
  buttonLabel,
  uploadImagesLabel,
  uploadImagesHint,
  handleChange,
  handleFileChange,
  handleSubmit,
}: {
  formData: { name: string; phone: string; email: string; city: string };
  images: File[];
  loading: boolean;
  errorMessage: string;
  buttonLabel: string;
  uploadImagesLabel: string;
  uploadImagesHint: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      onSubmit={handleSubmit}
      id="trichos-contact-us-form"
      className="space-y-4"
    >
      <div>
        <label htmlFor="w-contact-name" className="sr-only">
          Name
        </label>
        <input
          id="w-contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Name"
          className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          autoComplete="name"
          aria-required="true"
        />
      </div>
      <div>
        <label htmlFor="w-contact-phone" className="sr-only">
          Phone
        </label>
        <input
          id="w-contact-phone"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="Phone"
          className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          autoComplete="tel"
          aria-required="true"
        />
      </div>
      <div>
        <label htmlFor="w-contact-email" className="sr-only">
          Email
        </label>
        <input
          id="w-contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="Email"
          className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          autoComplete="email"
          aria-required="true"
        />
      </div>
      <div>
        <label htmlFor="w-contact-city" className="sr-only">
          City
        </label>
        <input
          id="w-contact-city"
          type="text"
          name="city"
          value={formData.city}
          onChange={handleChange}
          required
          placeholder="City"
          className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          autoComplete="address-level2"
          aria-required="true"
        />
      </div>
      <div>
        <label
          htmlFor="w-contact-images"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          {uploadImagesLabel}
        </label>
        <input
          id="w-contact-images"
          type="file"
          name="images"
          accept={ALLOWED_ACCEPT}
          multiple
          onChange={handleFileChange}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 file:mr-3 file:rounded file:border-0 file:bg-violet-100 file:px-3 file:py-1.5 file:text-violet-700 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          aria-describedby="w-contact-images-hint"
        />
        <p id="w-contact-images-hint" className="mt-1 text-xs text-gray-500">
          {uploadImagesHint}
        </p>
        {images.length > 0 && (
          <p className="mt-1 text-xs text-violet-700">
            {images.length} image(s) selected
          </p>
        )}
      </div>
      <div className="flex justify-center pt-2">
        <button
          type="submit"
          disabled={loading}
          className={`rounded-2xl border border-black bg-black px-8 py-3 text-base font-semibold text-white transition-colors ${
            loading
              ? "cursor-not-allowed border-gray-400 bg-gray-400 text-gray-600"
              : "hover:bg-neutral-900"
          }`}
          aria-busy={loading}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg
                className="size-5 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Sending…
            </span>
          ) : (
            buttonLabel
          )}
        </button>
      </div>
      {errorMessage && (
        <p className="text-center text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
      <p className="text-center text-xs text-gray-600">
        By submitting, you agree to be contacted by Trichos. This site is
        protected by reCAPTCHA and the Google{" "}
        <Link
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-600 underline"
        >
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-600 underline"
        >
          Terms of Service
        </Link>{" "}
        apply.
      </p>
    </form>
  );
}
